import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { useCompany } from "@/contexts/CompanyContext";
import { useTheme } from "@/contexts/ThemeContext";
import { COMPANY_COLORS, COMPANY_LABELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Truck, LayoutDashboard, Users, Car, CreditCard, FileSpreadsheet,
  Receipt, Upload, FileText, CalendarClock, Moon, Sun, LogOut, ChevronDown, Settings, Split, Send, Fuel, Wallet, FolderOpen, HardHat, TrendingUp, Calculator, Menu,
} from "lucide-react";

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/padroncini", icon: Users, label: "Padroncini" },
  { to: "/dipendenti", icon: HardHat, label: "Dipendenti padroncini", sub: true, testid: "nav-dipendenti" },
  { to: "/veicoli", icon: Car, label: "Veicoli" },
  { to: "/tessere", icon: CreditCard, label: "Tessere/Telepass", testid: "nav-tessere" },
  { to: "/consumi", icon: Fuel, label: "Consumi importati", sub: true },
  { to: "/clienti", icon: Users, label: "Clienti" },
  { to: "/prima-nota", icon: FileSpreadsheet, label: "Prima Nota" },
  { to: "/import", icon: Upload, label: "Import" },
  { to: "/ripartizione", icon: Split, label: "Ripartizione" },
  { to: "/conteggi", icon: Receipt, label: "Conteggi" },
  { to: "/invii", icon: Send, label: "Invii" },
  { to: "/scadenzario", icon: CalendarClock, label: "Scadenzario" },
  { to: "/pagamenti", icon: Wallet, label: "Pagamenti da fare", sub: true, testid: "nav-pagamenti" },
  { to: "/documenti", icon: FolderOpen, label: "Documenti azienda", testid: "nav-documenti" },
  { to: "/investimento", icon: TrendingUp, label: "Tabella investimento", testid: "nav-investimento" },
  { to: "/risultato", icon: Calculator, label: "Risultato fine mese", testid: "nav-risultato" },
];

const NavList = ({ testPrefix = "" }) => (
  <nav className="px-2 space-y-0.5">
    {NAV.map((n) => (
      <NavLink key={n.to} to={n.to} end={n.to === "/"}
               data-testid={`${testPrefix}${n.testid || `nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}`}
               className={({ isActive }) =>
                 `flex items-center gap-2.5 py-1.5 rounded-sm font-heading transition-colors ${n.sub ? "ml-5 pl-2.5 pr-2 text-xs border-l border-border" : "px-2.5 text-sm"} ${
                   isActive ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted"
                 }`
               }>
        <n.icon className={n.sub ? "w-3.5 h-3.5" : "w-4 h-4"} /> {n.label}
      </NavLink>
    ))}
  </nav>
);

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { company, setCompany } = useCompany();
  const { theme, toggle } = useTheme();
  const companyColor = COMPANY_COLORS[company] || "#334155";
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setMenuOpen(false); }, [pathname]);

  const companies = [
    { code: "all", name: "Consolidato" },
    { code: "golmar", name: "Golmar S.r.l." },
    { code: "vitam", name: "Vitam S.r.l." },
    { code: "rotta66", name: "ROTTA66" },
    { code: "fmg", name: "Consorzio FMG" },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* Header */}
      <header className="h-14 border-b border-border bg-card flex items-center px-2 sm:px-4 gap-2 sm:gap-4 sticky top-0 z-30">
        <Button variant="ghost" size="icon" className="h-9 w-9 lg:hidden" onClick={() => setMenuOpen(true)} data-testid="mobile-menu-button">
          <Menu className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-sm flex items-center justify-center" style={{ backgroundColor: companyColor }}>
            <Truck className="w-5 h-5 text-white" />
          </div>
          <div className="font-heading font-black text-lg tracking-tight hidden sm:block">AUTOTRANS</div>
        </div>
        <div className="w-px h-6 bg-border hidden sm:block" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 gap-2 font-heading" data-testid="company-switcher">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: companyColor }} />
              {COMPANY_LABELS[company] || "Consolidato"}
              <ChevronDown className="w-3.5 h-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56">
            <DropdownMenuLabel>Azienda</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {companies.map((c) => (
              <DropdownMenuItem key={c.code} onClick={() => setCompany(c.code)}
                                data-testid={`company-option-${c.code}`}>
                <span className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: COMPANY_COLORS[c.code] }} />
                {c.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="flex-1" />
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={toggle} data-testid="theme-toggle">
          {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 gap-2 font-heading" data-testid="user-menu">
              <div className="w-6 h-6 rounded-full bg-secondary text-secondary-foreground text-xs flex items-center justify-center font-bold">
                {user?.username?.[0]?.toUpperCase()}
              </div>
              <span className="hidden md:inline">{user?.username}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="font-heading">{user?.full_name}</div>
              <div className="text-xs text-muted-foreground font-normal">{user?.role}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild><Link to="/profilo"><Settings className="w-4 h-4 mr-2" />Profilo & 2FA</Link></DropdownMenuItem>
            {user?.role === "admin" && (
              <DropdownMenuItem asChild><Link to="/utenti"><Users className="w-4 h-4 mr-2" />Utenti</Link></DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="logout-button">
              <LogOut className="w-4 h-4 mr-2" />Esci
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>
      <div className="industrial-strip" style={{ background: `repeating-linear-gradient(45deg, ${companyColor} 0 10px, transparent 10px 20px)`, opacity: 0.3 }} />

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="w-72 p-0 pt-10 overflow-y-auto" data-testid="mobile-menu">
          <SheetTitle className="px-4 pb-2 font-heading font-black">AUTOTRANS</SheetTitle>
          <NavList testPrefix="m-" />
        </SheetContent>
      </Sheet>

      {/* Content */}
      <div className="flex flex-1 min-h-0">
        <aside className="w-56 border-r border-border bg-card py-3 flex-shrink-0 hidden lg:block">
          <NavList />
        </aside>
        <main className="autotrans-main flex-1 min-w-0 overflow-auto p-3 sm:p-5">{children}</main>
      </div>
    </div>
  );
}
